import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

const button = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none",
    "font-semibold tracking-tight transition-[transform,box-shadow,background-color,color] duration-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 focus-visible:ring-offset-cloud",
    "active:translate-y-px",
    "disabled:opacity-50 disabled:pointer-events-none disabled:active:translate-y-0",
  ],
  {
    variants: {
      variant: {
        // Primary CTA — green with forest press-down shadow ("toy" feel).
        primary: "bg-green text-white shadow-cta hover:bg-[color-mix(in_srgb,var(--kd-green)_92%,black)]",
        // Accent backup — sunny yellow over navy text.
        accent: "bg-green text-white shadow-cta hover:bg-[color-mix(in_srgb,var(--kd-green)_92%,black)]",
        // Sunny — secondary CTA, used where green would be too loud.
        sunny: "bg-sunny text-navy shadow-[0_2px_0_var(--kd-gold)] hover:bg-[color-mix(in_srgb,var(--kd-sunny)_92%,black)]",
        secondary:
          "bg-white text-navy ring-[1.5px] ring-line hover:bg-cloud",
        ghost: "text-navy hover:bg-cloud",
        danger: "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]",
      },
      size: {
        sm: "h-9 px-3.5 text-sm rounded-[12px]",
        md: "h-11 px-4 text-sm rounded-[14px]",
        lg: "h-13 px-5 text-[15px] rounded-[14px]",
        icon: "h-10 w-10 rounded-[12px]",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(button({ variant, size, block }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
